import { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { MotionValue } from "framer-motion";
import SceneTrack, { useSceneRender } from "../SceneTrack";
import SceneCanvas from "../SceneCanvas";
import { box, clamp, easeOut, lerp, place, range, scramble, seeded, snap } from "../morph";
import { toWeeks, useGitHubContributions } from "../../../hooks/useGitHubContributions";
import { BIO, GITHUB_USER, LOCATION, SKILLS } from "../../../data/profile";
import "./ProfileScene.css";

/*
 * 02 · Profile — "Grid Subdivision".
 *
 * One solid block carrying the bio statement. Five cuts run along the bento's
 * grid lines; as each finishes, the two sides snap apart by exactly one gap.
 * Then every tile boots its own content. Rigid, right-angled, stepped: the
 * opposite material to Contact's liquid split.
 */

const RADIUS = 14;
const HEAT_ALPHA = [0.06, 0.22, 0.42, 0.66, 0.92];

type Side = "t" | "r" | "b" | "l";
type Cut =
    | { a: number; b: number; axis: "v"; x: number; y1: number; y2: number }
    | { a: number; b: number; axis: "h"; y: number; x1: number; x2: number };
type Tile = { id: string; x: number; y: number; w: number; h: number; e: Partial<Record<Side, number>> };

/**
 * Where everything goes. Desktop is the 1200×720 bento; the phone version is
 * the same block cut into a single column (status and location side by side),
 * so the cuts run mostly across rather than down.
 */
interface Layout {
    phone: boolean;
    canvas: { width: number; height: number; navBand: number; pad: number; className: string };
    title: [number, number];
    sub: [number, number, number];
    /** Cut lines, in drawing order. `a`–`b` is the beat over which each draws. */
    cuts: readonly Cut[];
    /** Final tile rects; `e` maps each edge to the cut that runs along it. */
    tiles: Tile[];
    guides: number[];
    /** The solid block before any cut. */
    block: { x: number; y: number; w: number; h: number };
    /** Bio heading: where it starts (x, y, scale) and where it lands. */
    heading: [number, number, number, number, number];
    chips: { x: number; y: number; w: number };
    heatWeeks: number;
    /** How far the skill chips are scattered before they snap in. */
    scatter: number;
    /** Half the gap between tiles once cut. */
    gapHalf: number;
}

const DESKTOP: Layout = {
    phone: false,
    canvas: { width: 1200, height: 720, navBand: 56, pad: 48, className: "" },
    title: [96, 84],
    sub: [96, 152, 640],
    cuts: [
        { a: 0.08, b: 0.15, axis: "v", x: 600, y1: 220, y2: 508.67 },
        { a: 0.13, b: 0.2, axis: "h", y: 515.67, x1: 96, x2: 1104 },
        { a: 0.18, b: 0.25, axis: "h", y: 364.33, x1: 600, x2: 1104 },
        { a: 0.23, b: 0.3, axis: "v", x: 855.5, y1: 220, y2: 364.33 },
        { a: 0.28, b: 0.36, axis: "v", x: 600, y1: 515.67, y2: 660 },
    ],
    tiles: [
        { id: "bio", x: 96, y: 220, w: 497, h: 288.67, e: { r: 0, b: 1 } },
        { id: "status", x: 607, y: 220, w: 241.5, h: 137.33, e: { l: 0, b: 2, r: 3 } },
        { id: "loc", x: 862.5, y: 220, w: 241.5, h: 137.33, e: { l: 3, b: 2 } },
        { id: "clock", x: 607, y: 371.33, w: 497, h: 137.33, e: { l: 0, t: 2, b: 1 } },
        { id: "gh", x: 96, y: 522.67, w: 497, h: 137.33, e: { t: 1, r: 4 } },
        { id: "skills", x: 607, y: 522.67, w: 497, h: 137.33, e: { t: 1, l: 4 } },
    ],
    guides: [344.5, 600, 855.5],
    block: { x: 96, y: 220, w: 1008, h: 440 },
    heading: [280, 388, 1.6, 124, 266],
    chips: { x: 623, y: 562, w: 465 },
    heatWeeks: 35,
    scatter: 1,
    gapHalf: 7,
};

const PHONE: Layout = {
    phone: true,
    canvas: { width: 390, height: 844, navBand: 64, pad: 0, className: "scene-canvas--phone" },
    title: [20, 100],
    sub: [20, 150, 340],
    cuts: [
        { a: 0.08, b: 0.15, axis: "h", y: 397.5, x1: 20, x2: 370 },
        { a: 0.13, b: 0.2, axis: "v", x: 195, y1: 401, y2: 504 },
        { a: 0.18, b: 0.25, axis: "h", y: 507.5, x1: 20, x2: 370 },
        { a: 0.23, b: 0.3, axis: "h", y: 593.5, x1: 20, x2: 370 },
        { a: 0.28, b: 0.36, axis: "h", y: 703.5, x1: 20, x2: 370 },
    ],
    tiles: [
        { id: "bio", x: 20, y: 214, w: 350, h: 180, e: { b: 0 } },
        { id: "status", x: 20, y: 401, w: 168, h: 103, e: { t: 0, r: 1, b: 2 } },
        { id: "loc", x: 202, y: 401, w: 168, h: 103, e: { t: 0, l: 1, b: 2 } },
        { id: "clock", x: 20, y: 511, w: 350, h: 79, e: { t: 2, b: 3 } },
        { id: "gh", x: 20, y: 597, w: 350, h: 103, e: { t: 3, b: 4 } },
        { id: "skills", x: 20, y: 707, w: 350, h: 116, e: { t: 4 } },
    ],
    guides: [107.5, 195, 282.5],
    block: { x: 20, y: 214, w: 350, h: 609 },
    heading: [36, 470, 1.15, 36, 254],
    chips: { x: 36, y: 739, w: 318 },
    heatWeeks: 31,
    scatter: 0.5,
    gapHalf: 3.5,
};

const pad = (n: number) => String(n).padStart(2, "0");
const hms = (v: number) =>
    `${pad(Math.floor(v / 3600) % 24)}:${pad(Math.floor(v / 60) % 60)}:${pad(v % 60)}`;
const secondsToday = (d: Date) => d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();

export default function ProfileScene({ phone = false }: { phone?: boolean }) {
    const L = phone ? PHONE : DESKTOP;
    return <SceneTrack pin={240} tint="74, 190, 214">{(p) => <ProfileCanvas p={p} L={L} />}</SceneTrack>;
}

function ProfileCanvas({ p, L }: { p: MotionValue<number>; L: Layout }) {
    const { cuts: CUTS, tiles: TILES } = L;
    const HEAT_WEEKS = L.heatWeeks;
    const root = useRef<HTMLDivElement>(null);
    const { data, total, loading } = useGitHubContributions(GITHUB_USER);
    const weeks = toWeeks(data, HEAT_WEEKS);

    // Where each skill chip starts, scattered above its card.
    const scatter = useMemo(() => {
        const rnd = seeded(7);
        return SKILLS.map(() => ({ dx: (rnd() * 200 - 140) * L.scatter, dy: -(40 + rnd() * 120) * L.scatter, r: rnd() * 50 - 25 }));
    }, [L]);

    const els = useRef<{
        tiles: HTMLElement[];
        labels: (HTMLElement | null)[];
        cuts: HTMLElement[];
        guides: HTMLElement[];
        cols: HTMLElement[];
        chips: HTMLElement[];
        q: (k: string) => HTMLElement;
    } | null>(null);

    useLayoutEffect(() => {
        const r = root.current;
        if (!r) return;
        const all = (s: string) => Array.from(r.querySelectorAll<HTMLElement>(s));
        const tiles = all(".pf-tile");
        els.current = {
            tiles,
            labels: tiles.map((t) => t.querySelector<HTMLElement>(".scene-label")),
            cuts: all(".pf-cut"),
            guides: all(".pf-guide"),
            cols: all(".pf-heat-col"),
            chips: all(".pf-chip"),
            q: (k) => r.querySelector<HTMLElement>(`[data-pf="${k}"]`)!,
        };
    }, [loading]);

    const sep = useRef([0, 0, 0, 0, 0]);

    useSceneRender(
        p,
        (v) => {
            const E = els.current;
            if (!E) return;
            const s = sep.current;

            CUTS.forEach((c, i) => {
                const t = range(v, c.a, c.b);
                const el = E.cuts[i];
                el.style.transform = c.axis === "v" ? `scaleY(${t})` : `scaleX(${t})`;
                el.style.opacity = String(t <= 0 ? 0 : 1 - range(v, c.b + 0.02, c.b + 0.08));
                s[i] = snap(range(v, c.b, c.b + 0.05));
            });

            // Each tile reaches across a cut by half a gap until that cut has
            // separated, so before the cuts the tiles read as one solid block.
            TILES.forEach((tile, i) => {
                const el = E.tiles[i];
                const ext = (side: Side) => (tile.e[side] === undefined ? 0 : L.gapHalf * (1 - s[tile.e[side]!]));
                const sv = (side: Side) => (tile.e[side] === undefined ? 1 : clamp(s[tile.e[side]!]));
                box(
                    el,
                    tile.x - ext("l"),
                    tile.y - ext("t"),
                    tile.w + ext("l") + ext("r"),
                    tile.h + ext("t") + ext("b")
                );
                const rr = (a: Side, b: Side) => RADIUS * Math.min(sv(a), sv(b));
                el.style.borderRadius = `${rr("t", "l")}px ${rr("t", "r")}px ${rr("b", "r")}px ${rr("b", "l")}px`;
                const m = Math.min(...Object.values(tile.e).map((ci) => clamp(s[ci as number])));
                el.style.borderColor = `rgba(255,255,255,${0.105 * m})`;
                const lbl = E.labels[i];
                if (lbl) lbl.style.opacity = String(m);
            });

            E.q("mono").style.opacity = String(1 - range(v, 0.34, 0.38));
            E.guides.forEach((g) => (g.style.opacity = String(1 - range(v, 0.08, 0.2))));

            const kb = easeOut(range(v, 0.08, 0.2));
            const [hx0, hy0, hs0, hx1, hy1] = L.heading;
            place(E.q("heading"), lerp(hx0, hx1, kb), lerp(hy0, hy1, kb), lerp(hs0, 1, kb));
            scramble(E.q("who"), BIO.label, range(v, 0.18, 0.24));
            E.q("para").style.opacity = String(range(v, 0.2, 0.28));

            E.q("dot").style.transform = `scale(${snap(range(v, 0.4, 0.44))})`;
            E.q("status").style.opacity = String(range(v, 0.4, 0.45));
            E.q("statusSub").style.opacity = String(range(v, 0.42, 0.47));

            let t = easeOut(range(v, 0.42, 0.52));
            E.q("country").style.opacity = String(range(v, 0.42, 0.46));
            const coord = E.q("coord");
            coord.style.opacity = String(range(v, 0.42, 0.44));
            coord.textContent = `${(LOCATION.lat * t).toFixed(2)}°N · ${(LOCATION.lon * t).toFixed(2)}°E`;

            t = easeOut(range(v, 0.44, 0.54));
            const now = new Date();
            const time = E.q("time");
            time.textContent = hms(Math.round(secondsToday(now) * t));
            time.style.opacity = String(range(v, 0.44, 0.46));
            E.q("day").style.opacity = String(range(v, 0.5, 0.54));

            // Heatmap fills week by week, left to right.
            t = range(v, 0.48, 0.66);
            const n = E.cols.length;
            E.cols.forEach((col, i) => (col.style.opacity = String(clamp(t * n - i))));
            E.q("total").style.opacity = String(range(v, 0.6, 0.66));

            E.chips.forEach((chip, i) => {
                const k = range(v, 0.56 + i * 0.01, 0.64 + i * 0.01);
                const b = snap(k);
                const sc = scatter[i];
                chip.style.transform = `translate(${sc.dx * (1 - b)}px,${sc.dy * (1 - b)}px) rotate(${sc.r * (1 - b)}deg)`;
                chip.style.opacity = String(range(k, 0, 0.3));
            });
        },
        [loading, scatter]
    );

    // Once the clock has counted up to the real time, keep it ticking.
    useEffect(() => {
        const id = window.setInterval(() => {
            const E = els.current;
            if (!E || p.get() < 0.54) return;
            const now = new Date();
            E.q("time").textContent = hms(secondsToday(now));
        }, 1000);
        return () => window.clearInterval(id);
    }, [p]);

    const today = new Date().toLocaleDateString("en-US", { weekday: "long" });

    return (
        <SceneCanvas tint="74, 190, 214" marker={{ index: 2, label: "Profile" }} {...L.canvas}>
            <div ref={root} className={L.phone ? "pf pf--phone" : "pf"}>
                <h1 className="mx-abs scene-title" style={{ transform: `translate(${L.title[0]}px,${L.title[1]}px)` }}>
                    About <span>Me</span>
                </h1>
                <p className="mx-abs scene-sub" style={{ transform: `translate(${L.sub[0]}px,${L.sub[1]}px)`, width: L.sub[2] }}>
                    Passionate developer crafting digital experiences with code.
                </p>

                <div className="pf-tile pf-tile--bio">
                    <span className="pf-who mono" data-pf="who" />
                    <p className="pf-para" data-pf="para">{BIO.body}</p>
                </div>
                <div className="pf-tile pf-tile--status">
                    <span className="scene-label">Status</span>
                    <div className="pf-statrow" data-pf="status">
                        <i className="pf-dot" data-pf="dot" />
                        Available
                    </div>
                    <span className="pf-small" data-pf="statusSub">
                        Open to opportunities
                    </span>
                </div>
                <div className="pf-tile pf-tile--loc">
                    <span className="scene-label">Based in</span>
                    <div className="pf-big" data-pf="country">{LOCATION.country}</div>
                    <span className="pf-small mono" data-pf="coord" />
                </div>
                <div className="pf-tile pf-tile--clock">
                    <span className="scene-label">
                        <i className="pf-live" aria-hidden="true" />
                        Local time · Vietnam
                    </span>
                    <div className="pf-time mono" data-pf="time">00:00:00</div>
                    <span className="pf-small" data-pf="day">GMT+7 · {today}</span>
                </div>
                <div className="pf-tile pf-tile--gh">
                    <span className="scene-label">{L.phone ? "GitHub" : "GitHub · last 12 months"}</span>
                    <a
                        className="pf-total mono"
                        data-pf="total"
                        href={`https://github.com/${GITHUB_USER}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {total.toLocaleString()} contributions ↗
                    </a>
                    <div className="pf-heat" role="img" aria-label="GitHub contribution heatmap">
                        {(weeks.length ? weeks : Array.from({ length: HEAT_WEEKS }, () => [])).map((week, wi) => (
                            <div key={wi} className="pf-heat-col">
                                {Array.from({ length: 7 }).map((_, di) => {
                                    const day = week[di];
                                    return (
                                        <i
                                            key={di}
                                            style={{ background: `rgba(var(--scene-tint), ${HEAT_ALPHA[day ? day.level : 0]})` }}
                                            title={day ? `${day.count} contributions on ${day.date}` : undefined}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="pf-tile pf-tile--skills">
                    <span className="scene-label">Stack</span>
                </div>

                <div className="pf-chips" style={{ left: L.chips.x, top: L.chips.y, width: L.chips.w }}>
                    {SKILLS.map((s, i) => (
                        <span key={s} className="pf-chip mono" style={{ "--i": i } as CSSProperties}>
                            {s}
                        </span>
                    ))}
                </div>

                {CUTS.map((c, i) => (
                    <div
                        key={i}
                        className={`pf-cut pf-cut--${c.axis}`}
                        style={
                            c.axis === "v"
                                ? { left: c.x - 0.5, top: c.y1, height: c.y2 - c.y1 }
                                : { top: c.y - 0.5, left: c.x1, width: c.x2 - c.x1 }
                        }
                    />
                ))}
                {L.guides.map((x) => (
                    <div key={x} className="pf-guide" style={{ left: x, top: L.block.y, height: L.block.h }} />
                ))}
                <div className="pf-mono" data-pf="mono" style={{ left: L.block.x, top: L.block.y, width: L.block.w, height: L.block.h }} />
                <h3 className="mx-abs pf-heading" data-pf="heading">
                    {BIO.heading}
                </h3>
            </div>
        </SceneCanvas>
    );
}
